import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../utils/config';
import Layout from './Layout';
import YearSelector from './YearSelector';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { useAuth } from '../App';
import { SuccessPopup } from './SuccessPopup';
import api from '../utils/api';
import { Calendar, Plus, Search, AlertTriangle, Calculator, Euro, Building, ArrowUpDown, ArrowUp, ArrowDown, Printer, Trash2, RotateCcw, XCircle, Edit, Send, Lock, Unlock } from 'lucide-react';

// Campi tracciati per protezione modifiche manuali (devono coincidere con PROTECTED_FIELDS in backend/server.py)
const MANUAL_PROTECTED_FIELDS = [
  'guest_name', 'guest_total', 'platform_commission', 'partner_commission',
  'cleaning_cost', 'booking_type', 'payment_status', 'children_count',
  'guests_count', 'check_in', 'check_out', 'platform', 'booking_date',
  'guest_phone', 'guest_email', 'guest_nationality'
];
import { toast } from 'sonner';

const BookingsManager = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertyManagers, setPropertyManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [questuraPopup, setQuesturaPopup] = useState({ open: false, title: '', message: '', type: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCode, setSearchCode] = useState(() => new URLSearchParams(window.location.search).get('code') || '');
  const [searchProperty, setSearchProperty] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'check_in', direction: 'desc' });
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'cancelled', 'owner-payments'
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [ownerPaymentFilter, setOwnerPaymentFilter] = useState('all'); // 'all', 'confermata', 'da_pagare', 'pagato'
  const [ownerPayments, setOwnerPayments] = useState([]); // Dati per tab proprietari da pagare
  const [loadingOwnerPayments, setLoadingOwnerPayments] = useState(false);
  const [cancelModal, setCancelModal] = useState({ show: false, booking: null });
  const [restoreModal, setRestoreModal] = useState({ show: false, booking: null });
  const [editModal, setEditModal] = useState({ show: false, booking: null });
  const [editBookingData, setEditBookingData] = useState(null);
  const [suggestedProperty, setSuggestedProperty] = useState(null);
  const [filters, setFilters] = useState({
    platform: 'all',
    booking_type: 'all',
    payment_status: 'all',
    date_range: 'all',
    date_type: 'booking_date', // quale data filtrare: booking_date, check_in, check_out
    date_from: '',
    date_to: '',
    month: '', // filtro mese rapido
    incomplete: 'all' // 'all', 'incomplete', 'complete'
  });
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [marginWarning, setMarginWarning] = useState(null);
  const [showCollectionPopup, setShowCollectionPopup] = useState(false);
  const [todayCollections, setTodayCollections] = useState([]);
  const [showTodayFilter, setShowTodayFilter] = useState(false);
  const [todayStats, setTodayStats] = useState({ count: 0, total: 0 });
  const [showNewProperty, setShowNewProperty] = useState(false);
  const [newProperty, setNewProperty] = useState({
    name: '',
    city: '',
    address: '',
    region: '',
    postal_code: '',
    property_type: 'apartment',
    bedrooms: 1,
    bathrooms: 1,
    max_guests: 2,
    price_per_night: 0,
    cleaning_cost: 0,
    responsible_email: ''
  });
  const [newBooking, setNewBooking] = useState({
    booking_code: '',
    property_id: '',
    property_manager_id: '', // Responsabile immobile
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_nationality: '',
    guests_count: 1,
    submultiple_guests: null, // Auto-detect from booking_code
    booking_type: 'refundable',
    platform: 'holiday_world',
    booking_date: '',
    check_in: '',
    check_out: '',
    guest_total: 0,
    payment_status: 'to_collect',
    balance_due_date: '',
    owner_amount: 0,
    tourist_tax: 0,
    cleaning_cost: 0,
    linen_cost: 0,
    linen_cost_per_person: 0,
    guest_payment_method: 'card',
    tourist_tax_payment: 'on_site',
    cleaning_payment: 'on_site'
  });

  // Auto-calculate linen cost when guests_count or linen_cost_per_person changes
  useEffect(() => {
    if (newBooking.guests_count && newBooking.linen_cost_per_person) {
      const totalLinenCost = newBooking.guests_count * newBooking.linen_cost_per_person;
      if (totalLinenCost !== newBooking.linen_cost) {
        setNewBooking(prev => ({...prev, linen_cost: totalLinenCost}));
      }
    }
  }, [newBooking.guests_count, newBooking.linen_cost_per_person]);

  // Auto-detect submultiple from booking code
  useEffect(() => {
    if (newBooking.booking_code) {
      const regex = /\/S(\d+)$/;
      const match = newBooking.booking_code.match(regex);
      
      if (match) {
        const submultipleNumber = parseInt(match[1]);
        if (submultipleNumber !== newBooking.submultiple_guests) {
          setNewBooking(prev => ({
            ...prev,
            submultiple_guests: submultipleNumber,
            guests_count: submultipleNumber // Also update guests_count
          }));
        }
      } else {
        // If no /S pattern, reset submultiple_guests
        if (newBooking.submultiple_guests !== null) {
          setNewBooking(prev => ({...prev, submultiple_guests: null}));
        }
      }
    }
  }, [newBooking.booking_code]);

  // Calculate today's stats when today filter is active
  useEffect(() => {
    if (showTodayFilter && bookings.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const todayBookings = bookings.filter(booking => {
        const createdAt = new Date(booking.created_at);
        return createdAt >= today && createdAt < tomorrow && !booking.cancelled;
      });
      
      const totalAmount = todayBookings.reduce((sum, b) => sum + (b.guest_total || 0), 0);
      
      setTodayStats({
        count: todayBookings.length,
        total: totalAmount
      });
    }
  }, [showTodayFilter, bookings]);

  useEffect(() => {
    fetchBookings();
    fetchProperties();
    fetchPropertyManagers();
    checkTodayCollections();
  }, []);

  useEffect(() => {
    // Deep-link da notifica: ?code=... → trova la prenotazione e allinea anno/tab
    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) return;
    api.get(`/bookings?search=${encodeURIComponent(code)}`).then(r => {
      const b = (r.data || []).find(x => (x.booking_code || '').toLowerCase() === code.toLowerCase()) || (r.data || [])[0];
      if (!b) return;
      if (b.check_in) {
        const y = new Date(b.check_in).getFullYear();
        if (y && y !== currentYear) setCurrentYear(y);
      }
      if (b.cancelled) setActiveTab('cancelled');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Reload bookings when year changes
    fetchBookings();
    if (activeTab === 'owner-payments') {
      fetchOwnerPayments();
    }
  }, [currentYear]);

  useEffect(() => {
    // Reload owner payments when filter changes or tab becomes active
    if (activeTab === 'owner-payments') {
      fetchOwnerPayments();
    }
  }, [activeTab, ownerPaymentFilter]);

  useEffect(() => {
    // Auto-fill cleaning cost and property manager when property changes
    if (newBooking.property_id) {
      fetchCleaningCost(newBooking.property_id);
      fetchSuggestedManager(newBooking.property_id);
    }
  }, [newBooking.property_id]);

  const fetchSeq = useRef(0);
  const fetchBookings = async () => {
    const seq = ++fetchSeq.current;
    try {
      const response = await api.get(`/bookings?year=${currentYear}`);
      if (seq === fetchSeq.current) setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };


  const fetchOwnerPayments = async () => {
    setLoadingOwnerPayments(true);
    try {
      const statusParam = ownerPaymentFilter !== 'all' ? `&owner_payment_status=${ownerPaymentFilter}` : '';
      const response = await api.get(`/bookings/owner-payments?year=${currentYear}${statusParam}`);
      setOwnerPayments(response.data);
    } catch (error) {
      console.error('Error fetching owner payments:', error);
    } finally {
      setLoadingOwnerPayments(false);
    }
  };

  const updateOwnerPaymentStatus = async (bookingId, newStatus) => {
    try {
      await api.patch(`/bookings/${bookingId}/owner-payment-status?status=${newStatus}`);
      alert(`Stato aggiornato a: ${newStatus}`);
      // Reload data
      fetchOwnerPayments();
    } catch (error) {
      console.error('Error updating owner payment status:', error);
      alert('Errore nell\'aggiornamento dello stato');
    }
  };


  const checkTodayCollections = async () => {
    try {
      // Check if popup was already shown today
      const lastShown = localStorage.getItem('collection_popup_shown');
      const today = new Date().toDateString();
      
      if (lastShown === today) {
        return; // Already shown today
      }
      
      const token = localStorage.getItem('hw_token');
      const response = await api.get('/bookings/to-collect-today', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.count > 0) {
        setTodayCollections(response.data);
        setShowCollectionPopup(true);
        localStorage.setItem('collection_popup_shown', today);
      }
    } catch (error) {
      console.error('Error checking today collections:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await api.get('/properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchPropertyManagers = async () => {
    try {
      // Fetch both property managers and users with bookings_operator role
      const managersResponse = await api.get('/property-managers');
      const usersResponse = await api.get('/users');
      
      // Combine property managers and bookings operators
      const managers = managersResponse.data.map(m => ({
        id: m.id,
        name: `${m.first_name} ${m.last_name}`,
        email: m.email,
        type: 'property_manager'
      }));
      
      const bookingsOperators = usersResponse.data
        .filter(u => u.role === 'bookings_operator')
        .map(u => ({
          id: u.id,
          name: u.email.split('@')[0], // Use email prefix as name
          email: u.email,
          type: 'operator'
        }));
      
      setPropertyManagers([...managers, ...bookingsOperators]);
    } catch (error) {
      console.error('Error fetching property managers:', error);
    }
  };

  const fetchCleaningCost = async (propertyId) => {
    try {
      const response = await api.get(`/bookings/property/${propertyId}/cleaning-cost`);
      setNewBooking(prev => ({
        ...prev,
        cleaning_cost: response.data.cleaning_cost,
        linen_cost_per_person: response.data.linen_cost_per_person,
        linen_cost: (prev.guests_count || 1) * response.data.linen_cost_per_person
      }));
    } catch (error) {
      console.error('Error fetching cleaning cost:', error);
    }
  };

  const fetchSuggestedManager = async (propertyId) => {
    try {
      const response = await api.get(`/properties/${propertyId}/suggested-manager`);
      if (response.data.manager_id) {
        setNewBooking(prev => ({
          ...prev,
          property_manager_id: response.data.manager_id
        }));
        console.log(`Responsabile auto-compilato: ${response.data.name} (${response.data.source})`);
      }
    } catch (error) {
      console.error('Error fetching suggested manager:', error);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelModal.booking) return;
    
    try {
      const bookingId = cancelModal.booking.id;
      
      // Use query-based endpoint for special characters, path-based for normal IDs
      if (hasSpecialCharacters(bookingId)) {
        await api.patch(`/bookings/cancel?booking_id=${encodeURIComponent(bookingId)}`);
      } else {
        await api.patch(`/bookings/${bookingId}/cancel`);
      }
      
      setCancelModal({ show: false, booking: null });
      await fetchBookings(); // Refresh bookings
      alert('Prenotazione annullata con successo!');
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Errore durante l\'annullamento della prenotazione');
    }
  };

  const handleRestoreBooking = async () => {
    if (!restoreModal.booking) return;
    
    try {
      const bookingId = restoreModal.booking.id;
      
      // Use query-based endpoint for special characters, path-based for normal IDs
      if (hasSpecialCharacters(bookingId)) {
        await api.patch(`/bookings/restore?booking_id=${encodeURIComponent(bookingId)}`);
      } else {
        await api.patch(`/bookings/${bookingId}/restore`);
      }
      
      setRestoreModal({ show: false, booking: null });
      await fetchBookings(); // Refresh bookings
      alert('Prenotazione ripristinata con successo!');
    } catch (error) {
      console.error('Error restoring booking:', error);
      alert('Errore durante il ripristino della prenotazione');
    }
  };

  const handleEditBooking = (booking) => {
    setEditBookingData({
      ...booking,
      booking_date: new Date(booking.booking_date).toISOString().split('T')[0],
      check_in: new Date(booking.check_in).toISOString().split('T')[0],
      check_out: new Date(booking.check_out).toISOString().split('T')[0]
    });
    setEditModal({ show: true, booking });
    checkPropertyMatch(booking.booking_code);
  };

  // Reset (sblocca) uno o più campi manualmente modificati: il prossimo sync HW/Booking li sovrascriverà
  const handleResetManualFields = async (fields = null) => {
    if (!editModal.booking?.id) return;
    const lockedNow = editBookingData?.manually_edited_fields || [];
    if (lockedNow.length === 0) {
      toast.info('Nessun campo bloccato da sbloccare');
      return;
    }
    const targetLabel = fields ? `il campo "${fields.join(', ')}"` : 'TUTTI i campi bloccati';
    if (!window.confirm(`Vuoi davvero sbloccare ${targetLabel}?\n\nLa prossima importazione da Booking.com/HW sovrascriverà i valori con quelli del portale.`)) {
      return;
    }
    try {
      const bookingId = editModal.booking.id;
      const params = new URLSearchParams();
      if (fields && Array.isArray(fields)) {
        fields.forEach(f => params.append('fields', f));
      }
      const qs = params.toString() ? `?${params.toString()}` : '';
      const url = hasSpecialCharacters(bookingId)
        ? `/bookings/${encodeURIComponent(bookingId)}/reset-manual-fields${qs}`
        : `/bookings/${bookingId}/reset-manual-fields${qs}`;
      const res = await api.post(url);
      const stillLocked = res.data?.still_locked || [];
      const unlocked = res.data?.unlocked || [];
      // Aggiorna lo stato locale del modale senza ricaricare tutto
      setEditBookingData(prev => prev ? { ...prev, manually_edited_fields: stillLocked } : prev);
      toast.success(`Sbloccati ${unlocked.length} campi. Verranno ripresi dal portale al prossimo sync.`);
      // Ricarica anche la lista bookings in background
      fetchBookings();
    } catch (error) {
      console.error('Error resetting manual fields:', error);
      toast.error(error.response?.data?.detail || 'Errore nello sblocco dei campi');
    }
  };

  // Renderer riusabile per la <Label> di un campo protetto (con icona 🔒 + bottone Riprendi)
  const LockableLabel = ({ field, children }) => {
    const lockedFields = editBookingData?.manually_edited_fields || [];
    const isLocked = lockedFields.includes(field);
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <Label className="m-0">{children}</Label>
        {isLocked && (
          <>
            <span
              className="inline-flex items-center gap-1 text-[10px] text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 font-medium"
              title="Campo modificato manualmente. Non verrà sovrascritto dal sync Booking.com/HW."
              data-testid={`manual-lock-${field}`}
            >
              <Lock className="h-3 w-3" /> Manuale
            </span>
            <button
              type="button"
              onClick={() => handleResetManualFields([field])}
              className="inline-flex items-center gap-0.5 text-[10px] text-blue-700 hover:text-blue-900 underline"
              title="Sblocca questo campo: verrà ripreso dal portale al prossimo sync"
              data-testid={`manual-reset-${field}`}
            >
              <RotateCcw className="h-3 w-3" /> Riprendi
            </button>
          </>
        )}
      </div>
    );
  };

  // Helper function to check if booking ID contains special characters
  const hasSpecialCharacters = (bookingId) => {
    const result = bookingId.includes('/') || bookingId.includes('%') || bookingId.includes('+') || bookingId.includes('#');
    console.log(`hasSpecialCharacters('${bookingId}') = ${result}`);
    return result;
  };

  const checkPropertyMatch = async (bookingCode) => {
    if (!bookingCode || bookingCode.length < 4) {
      setSuggestedProperty(null);
      return;
    }

    try {
      // Match con prefisso più lungo verso il più corto (12 → 4 caratteri).
      // Codici immobili HW: 3 lettere + 4-9 cifre (es. IVE6901, ILA164702 = 9 chars).
      let matchedProperty = null;
      const bcUpper = bookingCode.toUpperCase();
      for (let len = Math.min(bcUpper.length, 12); len >= 4; len--) {
        const prefix = bcUpper.substring(0, len);
        matchedProperty = properties.find(p => (p.property_code || '').trim().toUpperCase() === prefix);
        if (matchedProperty) break;
      }
      setSuggestedProperty(matchedProperty || null);
    } catch (error) {
      console.error('Error checking property match:', error);
    }
  };

  // Auto-recognize property for new bookings based on booking code
  const handleBookingCodeChange = (bookingCode) => {
    // Auto-detect booking type from code (R or N)
    let autoBookingType = newBooking.booking_type; // Keep current if no R or N found
    const codeUpper = bookingCode.toUpperCase();
    
    if (codeUpper.includes('N')) {
      autoBookingType = 'non_refundable'; // N = Non rimborsabile
    } else if (codeUpper.includes('R')) {
      autoBookingType = 'refundable'; // R = Rimborsabile
    }
    
    setNewBooking({...newBooking, booking_code: bookingCode, booking_type: autoBookingType});
    
    // Auto-recognize property: prova da prefisso più lungo al più corto (12 → 4 chars)
    if (bookingCode && bookingCode.length >= 4) {
      let matchedProperty = null;
      const bcUpper = bookingCode.toUpperCase();
      for (let len = Math.min(bcUpper.length, 12); len >= 4; len--) {
        const prefix = bcUpper.substring(0, len);
        matchedProperty = properties.find(p => (p.property_code || '').trim().toUpperCase() === prefix);
        if (matchedProperty) break;
      }
      
      if (matchedProperty) {
        setNewBooking(prev => ({
          ...prev, 
          booking_code: bookingCode,
          booking_type: autoBookingType,
          property_id: matchedProperty.id
        }));
        console.log(`Auto-associata proprietà: ${matchedProperty.name} (${matchedProperty.property_code})`);
        console.log(`Tipo auto-rilevato: ${autoBookingType === 'refundable' ? 'Rimborsabile (R)' : 'Non Rimborsabile (N)'}`);
      }
    }
  };

  const handleSaveBookingEdit = async () => {
    if (!editBookingData) return;

    // Check if check-in date is before booking date
    const bookingDate = new Date(editBookingData.booking_date);
    const checkInDate = new Date(editBookingData.check_in);
    
    if (checkInDate < bookingDate) {
      const confirmMessage = `⚠️ ATTENZIONE\n\nLa data di check-in (${checkInDate.toLocaleDateString('it-IT')}) è antecedente alla data di arrivo prenotazione (${bookingDate.toLocaleDateString('it-IT')}).\n\nVuoi comunque procedere con la modifica?`;
      
      if (!window.confirm(confirmMessage)) {
        return; // User cancelled, don't proceed
      }
    }

    try {
      const bookingId = editModal.booking.id;
      const bookingData = {
        ...editBookingData,
        guest_total: parseFloat(editBookingData.guest_total) || 0,
        platform_commission: parseFloat(editBookingData.platform_commission) || 0,
        partner_commission: parseFloat(editBookingData.partner_commission) || 0,
        cleaning_cost: parseFloat(editBookingData.cleaning_cost) || 0,
        guests_count: parseInt(editBookingData.guests_count) || 1,
        children_count: parseInt(editBookingData.children_count) || 0
      };

      // Use query-based endpoint for special characters, path-based for normal IDs
      if (hasSpecialCharacters(bookingId)) {
        console.log(`Using query endpoint for booking ID: ${bookingId}`);
        await api.put(`/bookings/update?booking_id=${encodeURIComponent(bookingId)}`, bookingData);
      } else {
        console.log(`Using path endpoint for booking ID: ${bookingId}`);
        await api.put(`/bookings/${bookingId}`, bookingData);
      }

      setEditModal({ show: false, booking: null });
      setEditBookingData(null);
      await fetchBookings();
      alert('Prenotazione aggiornata con successo!');
    } catch (error) {
      console.error('Error updating booking:', error);
      alert(error.response?.data?.detail || 'Errore durante l\'aggiornamento della prenotazione');
    }
  };

  const handleCreateProperty = async () => {
    try {
      const propertyData = {
        ...newProperty,
        bedrooms: parseInt(newProperty.bedrooms),
        bathrooms: parseInt(newProperty.bathrooms),
        max_guests: parseInt(newProperty.max_guests),
        price_per_night: parseFloat(newProperty.price_per_night),
        cleaning_cost: parseFloat(newProperty.cleaning_cost)
      };

      const response = await api.post('/properties', propertyData);
      
      // Add new property to local list
      setProperties(prev => [...prev, response.data]);
      
      // Set as selected property in booking
      setNewBooking(prev => ({
        ...prev,
        property_id: response.data.id,
        cleaning_cost: response.data.cleaning_cost
      }));
      
      setShowNewProperty(false);
      
      // Reset property form
      setNewProperty({
        name: '',
        city: '',
        address: '',
        region: '',
        postal_code: '',
        property_type: 'apartment',
        bedrooms: 1,
        bathrooms: 1,
        max_guests: 2,
        price_per_night: 0,
        cleaning_cost: 0,
        responsible_email: ''
      });
      
      alert('Immobile creato e selezionato con successo!');
    } catch (error) {
      console.error('Error creating property:', error);
      alert('Errore nella creazione dell\'immobile');
    }
  };

  const calculateMargin = async () => {
    try {
      const response = await api.post('/bookings/calculate-margin', {
        guest_total: parseFloat(newBooking.guest_total),
        owner_amount: parseFloat(newBooking.owner_amount),
        tourist_tax: parseFloat(newBooking.tourist_tax),
        cleaning_cost: parseFloat(newBooking.cleaning_cost),
        guest_payment_method: newBooking.guest_payment_method,
        platform: newBooking.platform
      });
      
      const calculation = response.data;
      if (calculation.is_negative) {
        setMarginWarning({
          ...calculation,
          message: `ATTENZIONE: Il margine è negativo (€${calculation.margin.toFixed(2)}). Controlla i dati.`
        });
        return false;
      }
      
      setMarginWarning(null);
      return true;
    } catch (error) {
      console.error('Error calculating margin:', error);
      return false;
    }
  };

  const handleCreateBooking = async (e, forceCreate = false) => {
    e.preventDefault();
    
    if (!forceCreate) {
      const marginOk = await calculateMargin();
      if (!marginOk && marginWarning) {
        // Show warning, don't proceed
        return;
      }
      
      // Check if check-in date is before booking date
      const bookingDate = new Date(newBooking.booking_date);
      const checkInDate = new Date(newBooking.check_in);
      
      if (checkInDate < bookingDate) {
        const confirmMessage = `⚠️ ATTENZIONE\n\nLa data di check-in (${checkInDate.toLocaleDateString('it-IT')}) è antecedente alla data di arrivo prenotazione (${bookingDate.toLocaleDateString('it-IT')}).\n\nVuoi comunque procedere con l'inserimento?`;
        
        if (!window.confirm(confirmMessage)) {
          return; // User cancelled, don't proceed
        }
      }
    }

    try {
      const bookingData = {
        ...newBooking,
        booking_date: new Date(newBooking.booking_date).toISOString(),
        check_in: new Date(newBooking.check_in).toISOString(),
        check_out: new Date(newBooking.check_out).toISOString(),
        balance_due_date: newBooking.balance_due_date ? new Date(newBooking.balance_due_date).toISOString() : null,
        guests_count: parseInt(newBooking.guests_count) || 1,
        guest_total: parseFloat(newBooking.guest_total) || 0,
        owner_amount: parseFloat(newBooking.owner_amount) || 0,
        tourist_tax: parseFloat(newBooking.tourist_tax) || 0,
        // Clean empty strings to null to avoid Pydantic validation errors
        guest_email: newBooking.guest_email?.trim() || null,
        guest_phone: newBooking.guest_phone?.trim() || null,
        guest_nationality: newBooking.guest_nationality?.trim() || null
      };
      
      // DEBUG: Log what we're sending
      console.log('📤 Sending booking data:', JSON.stringify(bookingData, null, 2));

      const response = await api.post('/bookings', bookingData);
      console.log('✅ Booking created:', response.data);
      
      setShowNewBooking(false);
      setMarginWarning(null);
      resetNewBooking();
      fetchBookings();
    } catch (error) {
      console.error('Error creating booking:', error);
    }
  };

  const resetNewBooking = () => {
    setNewBooking({
      booking_code: '',
      property_id: '',
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      guest_nationality: '',
      guests_count: 1,
      submultiple_guests: null, // Auto-detect from booking_code
      booking_type: 'refundable',
      platform: 'holiday_world',
      booking_date: '',
      check_in: '',
      check_out: '',
      guest_total: 0,
      payment_status: 'to_collect',
      balance_due_date: '',
      owner_amount: 0,
      tourist_tax: 0,
      cleaning_cost: 0,
      linen_cost: 0,
      linen_cost_per_person: 0,
      guest_payment_method: 'card',
      tourist_tax_payment: 'on_site',
      cleaning_payment: 'on_site'
    });
  };

  const getPlatformLabel = (platform) => {
    const labels = {
      'holiday_world': 'Holiday World',
      'airbnb': 'Airbnb',
      'booking_com': 'Booking.com',
      'atraveo': 'Atraveo',
      'housinganywhere': 'Housinganywhere',
      'spotahome': 'Spotahome'
    };
    return labels[platform] || platform;
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      collected: { label: 'Incassato', className: 'bg-emerald-100 text-emerald-800' },
      to_collect: { label: 'Da Incassare', className: 'bg-amber-100 text-amber-800' },
      partial: { label: 'Parziale', className: 'bg-blue-100 text-blue-800' }
    };
    
    const config = statusConfig[status] || statusConfig.to_collect;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleMonthChange = (monthValue) => {
    if (!monthValue) {
      // Clear month filter
      setFilters({...filters, month: '', date_from: '', date_to: ''});
      return;
    }

    // monthValue is in format "YYYY-MM"
    const [year, month] = monthValue.split('-');
    const firstDay = new Date(parseInt(year), parseInt(month) - 1, 1);
    const lastDay = new Date(parseInt(year), parseInt(month), 0);
    
    // Format as YYYY-MM-DD for input date fields
    const dateFrom = `${year}-${month}-01`;
    const dateTo = `${year}-${month}-${lastDay.getDate().toString().padStart(2, '0')}`;
    
    setFilters({
      ...filters, 
      month: monthValue,
      date_from: dateFrom,
      date_to: dateTo
    });
  };

  const handlePrint = () => {
    // Create print window with filtered bookings
    const printWindow = window.open('', '_blank');
    
    // Build HTML for print
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Prenotazioni Holiday World - Stampa</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px;
            font-size: 10px;
          }
          h1 { 
            font-size: 18px; 
            margin-bottom: 10px;
            color: #1e3a8a;
          }
          .filters-info {
            margin-bottom: 15px;
            padding: 10px;
            background-color: #f0f9ff;
            border: 1px solid #bfdbfe;
            border-radius: 4px;
            font-size: 9px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th { 
            background-color: #1e3a8a; 
            color: white; 
            padding: 8px 4px;
            text-align: left;
            font-size: 9px;
            font-weight: bold;
            border: 1px solid #1e40af;
          }
          td { 
            padding: 6px 4px; 
            border: 1px solid #ddd;
            font-size: 9px;
          }
          tr:nth-child(even) { background-color: #f9fafb; }
          .totals {
            margin-top: 20px;
            padding: 15px;
            background-color: #1e3a8a;
            color: white;
            border-radius: 4px;
          }
          .totals-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
          }
          .total-item {
            text-align: center;
          }
          .total-label {
            font-size: 9px;
            opacity: 0.9;
          }
          .total-value {
            font-size: 12px;
            font-weight: bold;
            margin-top: 5px;
          }
          .badge {
            display: inline-block;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 8px;
            font-weight: bold;
          }
          .badge-success { background-color: #d1fae5; color: #065f46; }
          .badge-warning { background-color: #fef3c7; color: #92400e; }
          .badge-info { background-color: #dbeafe; color: #1e40af; }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>📊 Prenotazioni Holiday World</h1>
        <div class="filters-info">
          <strong>Filtri applicati:</strong> ${getFiltersDescription()}
          <br><strong>Data stampa:</strong> ${new Date().toLocaleDateString('it-IT', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Codice</th>
              <th>Alloggio</th>
              <th>Ospite</th>
              <th>Tipo</th>
              <th>Piattaforma</th>
              <th>Data Pren.</th>
              <th>Check-in</th>
              <th>Check-out</th>
              <th>Totale</th>
              <th>Stato</th>
              ${user?.role === 'admin' ? '<th>Proprietario</th><th>Margine</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${filteredBookings.map(booking => `
              <tr>
                <td>${booking.booking_code}</td>
                <td>${properties.find(p => p.id === booking.property_id)?.name || 'N/A'}</td>
                <td>${booking.guest_name}</td>
                <td>
                  <span class="badge ${booking.booking_type === 'refundable' ? 'badge-info' : 'badge-warning'}">
                    ${booking.booking_type === 'refundable' ? 'Rimb.' : 'Non Rimb.'}
                  </span>
                </td>
                <td>${getPlatformLabel(booking.platform)}</td>
                <td>${new Date(booking.booking_date).toLocaleDateString('it-IT')}</td>
                <td>${new Date(booking.check_in).toLocaleDateString('it-IT')}</td>
                <td>${new Date(booking.check_out).toLocaleDateString('it-IT')}</td>
                <td>€${booking.guest_total.toFixed(2)}</td>
                <td>
                  <span class="badge ${booking.payment_status === 'collected' ? 'badge-success' : 'badge-warning'}">
                    ${booking.payment_status === 'collected' ? 'Incassato' : 'Da Incassare'}
                  </span>
                </td>
                ${user?.role === 'admin' ? `
                  <td>€${(booking.owner_amount || 0).toFixed(2)}</td>
                  <td>€${(booking.margin || 0).toFixed(2)}</td>
                ` : ''}
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="totals">
          <div class="totals-grid">
            <div class="total-item">
              <div class="total-label">Prenotazioni</div>
              <div class="total-value">${totals.count}</div>
            </div>
            <div class="total-item">
              <div class="total-label">Totale</div>
              <div class="total-value">€${totals.guest_total.toLocaleString('it-IT', {minimumFractionDigits: 2})}</div>
            </div>
            <div class="total-item">
              <div class="total-label">Incassato</div>
              <div class="total-value">€${totals.collected.toLocaleString('it-IT', {minimumFractionDigits: 2})}</div>
            </div>
            <div class="total-item">
              <div class="total-label">Da Incassare</div>
              <div class="total-value">€${totals.to_collect.toLocaleString('it-IT', {minimumFractionDigits: 2})}</div>
            </div>
            ${user?.role === 'admin' ? `
              <div class="total-item">
                <div class="total-label">Proprietario</div>
                <div class="total-value">€${totals.owner_amount.toFixed(2)}</div>
              </div>
              <div class="total-item">
                <div class="total-label">Margine</div>
                <div class="total-value">€${totals.margin.toFixed(2)}</div>
              </div>
            ` : ''}
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
            // Optionally close window after print
            // window.onafterprint = function() { window.close(); }
          }
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const getFiltersDescription = () => {
    const parts = [];
    
    if (searchCode) parts.push(`Codice: "${searchCode}"`);
    if (searchTerm) parts.push(`Ospite: "${searchTerm}"`);
    if (searchProperty) parts.push(`Immobile: "${searchProperty}"`);
    if (filters.platform !== 'all') parts.push(`Piattaforma: ${getPlatformLabel(filters.platform)}`);
    if (filters.booking_type !== 'all') parts.push(`Tipo: ${filters.booking_type === 'refundable' ? 'Rimborsabile' : 'Non Rimborsabile'}`);
    if (filters.payment_status !== 'all') parts.push(`Stato: ${filters.payment_status === 'collected' ? 'Incassato' : 'Da Incassare'}`);
    if (filters.date_from && filters.date_to) {
      const dateType = filters.date_type === 'booking_date' ? 'Data Prenotazione' : 
                       filters.date_type === 'check_in' ? 'Check-in' : 'Check-out';
      parts.push(`${dateType}: ${new Date(filters.date_from).toLocaleDateString('it-IT')} - ${new Date(filters.date_to).toLocaleDateString('it-IT')}`);
    }
    
    return parts.length > 0 ? parts.join(' | ') : 'Nessun filtro applicato';
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  const filteredBookings = bookings.filter(booking => {
    // Filter by cancelled status based on active tab
    const matchesCancelledStatus = activeTab === 'active' ? !booking.cancelled : booking.cancelled;
    
    const matchesCode = !searchCode || booking.booking_code?.toLowerCase().includes(searchCode.toLowerCase());
    const matchesSearch = !searchTerm || booking.guest_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by property name
    const property = properties.find(p => p.id === booking.property_id);
    const matchesProperty = !searchProperty || property?.name?.toLowerCase().includes(searchProperty.toLowerCase());
    
    const matchesPlatform = filters.platform === 'all' || booking.platform === filters.platform;
    const matchesType = filters.booking_type === 'all' || booking.booking_type === filters.booking_type;
    const matchesPayment = filters.payment_status === 'all' || booking.payment_status === filters.payment_status;
    
    // Filter by incomplete status
    let matchesIncomplete = true;
    if (filters.incomplete === 'incomplete') {
      matchesIncomplete = booking.is_incomplete === true;
    } else if (filters.incomplete === 'complete') {
      matchesIncomplete = !booking.is_incomplete;
    }
    
    // Filter "Oggi" - prenotazioni create oggi
    let matchesTodayFilter = true;
    if (showTodayFilter) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const createdAt = new Date(booking.created_at);
      matchesTodayFilter = createdAt >= today && createdAt < tomorrow;
    }
    
    // Date filtering
    let matchesDateRange = true;
    if (filters.date_from || filters.date_to) {
      const dateField = filters.date_type; // booking_date, check_in, or check_out
      const bookingDate = new Date(booking[dateField]);
      
      if (filters.date_from) {
        const fromDate = new Date(filters.date_from);
        fromDate.setHours(0, 0, 0, 0);
        matchesDateRange = matchesDateRange && bookingDate >= fromDate;
      }
      
      if (filters.date_to) {
        const toDate = new Date(filters.date_to);
        toDate.setHours(23, 59, 59, 999);
        matchesDateRange = matchesDateRange && bookingDate <= toDate;
      }
    }
    
    return matchesCancelledStatus && matchesCode && matchesSearch && matchesProperty && matchesPlatform && matchesType && matchesPayment && matchesDateRange && matchesTodayFilter && matchesIncomplete;
  }).sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    
    // Handle date fields specially - convert to Date objects for proper comparison
    const dateFields = ['booking_date', 'check_in', 'check_out', 'created_at'];
    if (dateFields.includes(sortConfig.key)) {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Calculate totals for filtered bookings
  const totals = filteredBookings.reduce((acc, booking) => {
    const amount = booking.guest_total || 0;
    const collected = booking.payment_status === 'collected' ? amount : 0;
    const toCollect = booking.payment_status === 'to_collect' ? amount : 0;
    
    return {
      guest_total: acc.guest_total + amount,
      collected: acc.collected + collected,
      to_collect: acc.to_collect + toCollect,
      owner_amount: acc.owner_amount + (booking.owner_amount || 0),
      tourist_tax: acc.tourist_tax + (booking.tourist_tax || 0),
      cleaning_cost: acc.cleaning_cost + (booking.cleaning_cost || 0),
      card_cost: acc.card_cost + (booking.card_cost || 0),
      platform_commission: acc.platform_commission + (booking.platform_commission || 0),
      partner_commission: acc.partner_commission + (booking.partner_commission || 0),
      margin: acc.margin + (booking.margin || 0),
      count: acc.count + 1
    };
  }, {
    guest_total: 0,
    collected: 0,
    to_collect: 0,
    owner_amount: 0,
    tourist_tax: 0,
    cleaning_cost: 0,
    card_cost: 0,
    platform_commission: 0,
    partner_commission: 0,
    margin: 0,
    count: 0
  });

  if (loading) {
    return (
      <Layout title="Gestione Prenotazioni">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestione Prenotazioni">
      <SuccessPopup open={questuraPopup.open} onClose={() => setQuesturaPopup(p => ({...p, open: false}))} title={questuraPopup.title} message={questuraPopup.message} type={questuraPopup.type} details={questuraPopup.details || []} />
      {/* Cancel Confirmation Modal */}
      <Dialog open={cancelModal.show} onOpenChange={(open) => setCancelModal({ show: open, booking: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              <span>Conferma Annullamento</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Sei sicuro di voler annullare questa prenotazione?
            </p>
            {cancelModal.booking && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                <div><strong>Codice:</strong> {cancelModal.booking.booking_code}</div>
                <div><strong>Ospite:</strong> {cancelModal.booking.guest_name}</div>
                <div><strong>Check-in:</strong> {new Date(cancelModal.booking.check_in).toLocaleDateString('it-IT')}</div>
                <div><strong>Totale:</strong> €{cancelModal.booking.guest_total?.toFixed(2)}</div>
              </div>
            )}
            <p className="text-sm text-gray-500">
              La prenotazione sarà spostata nella sezione "Prenotazioni Annullate" e potrà essere ripristinata in seguito se necessario.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setCancelModal({ show: false, booking: null })}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelBooking}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Conferma Annullamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Modal */}
      <Dialog open={restoreModal.show} onOpenChange={(open) => setRestoreModal({ show: open, booking: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-blue-600">
              <RotateCcw className="h-5 w-5" />
              <span>Conferma Ripristino</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Sei sicuro di voler ripristinare questa prenotazione annullata?
            </p>
            {restoreModal.booking && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                <div><strong>Codice:</strong> {restoreModal.booking.booking_code}</div>
                <div><strong>Ospite:</strong> {restoreModal.booking.guest_name}</div>
                <div><strong>Check-in:</strong> {new Date(restoreModal.booking.check_in).toLocaleDateString('it-IT')}</div>
                <div><strong>Totale:</strong> €{restoreModal.booking.guest_total?.toFixed(2)}</div>
                <div className="text-red-600 text-xs">
                  Annullata il {new Date(restoreModal.booking.cancelled_date).toLocaleDateString('it-IT')} da {restoreModal.booking.cancelled_by}
                </div>
              </div>
            )}
            <p className="text-sm text-gray-500">
              La prenotazione tornerà nella sezione "Prenotazioni Attive".
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setRestoreModal({ show: false, booking: null })}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={handleRestoreBooking}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Conferma Ripristino
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-2 fade-in pb-24">
        {/* Barra totali fissa in basso dello schermo */}
        {filteredBookings.length > 0 && user?.role !== 'bookings_operator' && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-2xl border-t border-blue-700">
            <div className="container mx-auto px-6 py-3">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center space-x-6">
                  <div className="font-bold text-white">
                    📊 TOTALI ({totals.count} prenotazioni)
                  </div>
                  <div className="font-semibold text-blue-100">
                    Totale: <span className="font-bold text-white">€{totals.guest_total.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className="font-semibold text-emerald-200">
                    Incassato: <span className="font-bold text-white">€{totals.collected.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className="font-semibold text-orange-200">
                    Da Incassare: <span className="font-bold text-white">€{totals.to_collect.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                  </div>
                </div>
                
                {user?.role === 'admin' && (
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="text-blue-100">
                      Proprietario: <span className="font-bold text-white">€{totals.owner_amount.toFixed(2)}</span>
                    </div>
                    <div className="text-blue-100">
                      Tasse: <span className="font-bold text-white">€{totals.tourist_tax.toFixed(2)}</span>
                    </div>
                    <div className="text-blue-100">
                      Pulizie: <span className="font-bold text-white">€{totals.cleaning_cost.toFixed(2)}</span>
                    </div>
                    <div className="text-amber-200">
                      Carta: <span className="font-bold">€{totals.card_cost.toFixed(2)}</span>
                    </div>
                    <div className="text-purple-200">
                      Piattaforma: <span className="font-bold">€{totals.platform_commission.toFixed(2)}</span>
                    </div>
                    <div className="text-cyan-200">
                      Partner: <span className="font-bold">€{totals.partner_commission.toFixed(2)}</span>
                    </div>
                    <div className="ml-4 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <span className="font-medium text-white">MARGINE: </span>
                      <span className={`font-bold text-lg ${totals.margin >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                        €{totals.margin.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Header with actions - compact */}
        <Card className="p-3 border-0 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold text-slate-900">Prenotazioni</h2>
              <Badge variant="outline" className="text-xs">{filteredBookings.length} risultati</Badge>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button 
                onClick={() => setShowTodayFilter(!showTodayFilter)}
                variant={showTodayFilter ? "default" : "outline"}
                size="sm"
                className={showTodayFilter 
                  ? "bg-green-600 hover:bg-green-700 text-white" 
                  : "border-green-600 text-green-600 hover:bg-green-50"
                }
              >
                <Calendar className="h-3.5 w-3.5 mr-1" />
                Oggi
                {showTodayFilter && todayStats.count > 0 && (
                  <Badge className="ml-1 bg-white text-green-600 text-xs px-1">
                    {todayStats.count}
                  </Badge>
                )}
              </Button>
              
              <Button 
                onClick={handlePrint}
                variant="outline"
                size="sm"
                className="border-blue-900 text-blue-900 hover:bg-blue-50"
              >
                <Printer className="h-3.5 w-3.5 mr-1" />
                Stampa
              </Button>
              
              <Dialog open={showNewBooking} onOpenChange={setShowNewBooking}>
                <DialogTrigger asChild>
                  <Button 
                    size="sm"
                    className="bg-blue-900 hover:bg-blue-800"
                    data-testid="add-booking-btn"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Nuova
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5" />
                    <span>Nuova Prenotazione</span>
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={(e) => handleCreateBooking(e)} className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Codice Prenotazione *</Label>
                      <Input
                        value={newBooking.booking_code}
                        onChange={(e) => handleBookingCodeChange(e.target.value)}
                        required
                        placeholder="242320250001"
                        data-testid="booking-code-input"
                      />
                      {newBooking.property_id && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Immobile auto-associato
                        </p>
                      )}
                      {newBooking.submultiple_guests && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                          <p className="text-xs text-blue-800 font-semibold">
                            🔢 Sottomultiplo Rilevato: {newBooking.submultiple_guests} persone
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            Auto-rilevato dal codice prenotazione (termina con /S{newBooking.submultiple_guests})
                          </p>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Alloggio *</Label>
                      <div className="flex gap-2">
                        <SearchableSelect
                          value={newBooking.property_id}
                          onValueChange={(value) => setNewBooking({...newBooking, property_id: value})}
                          placeholder="Cerca e seleziona alloggio..."
                          options={properties.map(property => ({
                            value: property.id,
                            label: `${property.name} - ${property.city} (${property.property_code || 'N/A'})`
                          }))}
                          searchPlaceholder="Cerca immobile per nome, città o codice..."
                          className="flex-1"
                          data-testid="property-searchable-select"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowNewProperty(true)}
                          className="px-3"
                          data-testid="add-new-property-btn"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Cerca per nome, città o codice immobile. + per aggiungere nuovo immobile
                      </p>
                    </div>
                    
                    <div>
                      <Label>Responsabile</Label>
                      <SearchableSelect
                        value={newBooking.property_manager_id}
                        onValueChange={(value) => setNewBooking({...newBooking, property_manager_id: value})}
                        placeholder="Seleziona responsabile..."
                        options={propertyManagers.map(manager => ({
                          value: manager.id,
                          label: `${manager.name} (${manager.email})`
                        }))}
                        searchPlaceholder="Cerca responsabile per nome o email..."
                        className="w-full"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Auto-compilato dall'immobile o default: prenotazioni@holidayw.eu
                      </p>
                    </div>
                    <div>
                      <Label>Piattaforma *</Label>
                      <Select 
                        value={newBooking.platform}
                        onValueChange={(value) => setNewBooking({...newBooking, platform: value})}
                      >
                        <SelectTrigger data-testid="platform-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="holiday_world">Holiday World (0%)</SelectItem>
                          <SelectItem value="airbnb">Airbnb (3%)</SelectItem>
                          <SelectItem value="booking_com">Booking.com (18%)</SelectItem>
                          <SelectItem value="atraveo">Atraveo (15%)</SelectItem>
                          <SelectItem value="housinganywhere">Housinganywhere (0%)</SelectItem>
                          <SelectItem value="spotahome">Spotahome (0%)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Guest Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label>Nome Ospite *</Label>
                      <Input
                        value={newBooking.guest_name}
                        onChange={(e) => setNewBooking({...newBooking, guest_name: e.target.value})}
                        required
                        data-testid="guest-name-input"
                      />
                    </div>
                    <div>
                      <Label>Email Ospite</Label>
                      <Input
                        type="email"
                        value={newBooking.guest_email}
                        onChange={(e) => setNewBooking({...newBooking, guest_email: e.target.value})}
                        data-testid="guest-email-input"
                      />
                    </div>
                    <div>
                      <Label>Telefono Ospite</Label>
                      <Input
                        type="tel"
                        value={newBooking.guest_phone}
                        onChange={(e) => setNewBooking({...newBooking, guest_phone: e.target.value})}
                        placeholder="+39 123 456 7890"
                        data-testid="guest-phone-input"
                      />
                    </div>
                    <div>
                      <Label>Nazionalità Ospite</Label>
                      <select
                        value={newBooking.guest_nationality}
                        onChange={(e) => setNewBooking({...newBooking, guest_nationality: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="guest-nationality-input"
                      >
                        <option value="">Seleziona nazionalità</option>
                        <option value="Italia">🇮🇹 Italia</option>
                        <option value="Francia">🇫🇷 Francia</option>
                        <option value="Germania">🇩🇪 Germania</option>
                        <option value="Regno Unito">🇬🇧 Regno Unito</option>
                        <option value="Spagna">🇪🇸 Spagna</option>
                        <option value="Stati Uniti">🇺🇸 Stati Uniti</option>
                        <option value="Svizzera">🇨🇭 Svizzera</option>
                        <option value="Austria">🇦🇹 Austria</option>
                        <option value="Paesi Bassi">🇳🇱 Paesi Bassi</option>
                        <option value="Belgio">🇧🇪 Belgio</option>
                        <option value="Polonia">🇵🇱 Polonia</option>
                        <option value="Russia">🇷🇺 Russia</option>
                        <option value="Cina">🇨🇳 Cina</option>
                        <option value="Giappone">🇯🇵 Giappone</option>
                        <option value="Brasile">🇧🇷 Brasile</option>
                        <option value="Argentina">🇦🇷 Argentina</option>
                        <option value="Canada">🇨🇦 Canada</option>
                        <option value="Australia">🇦🇺 Australia</option>
                        <option value="Altro">🌍 Altro</option>
                      </select>
                      <p className="text-xs text-slate-500 mt-1">
                        🌍 Utile per messaggistica multilingue futura
                      </p>
                    </div>
                    <div>
                      <Label>Numero Ospiti *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newBooking.guests_count}
                        onChange={(e) => setNewBooking({...newBooking, guests_count: e.target.value})}
                        required
                        data-testid="guests-count-input"
                      />
                      
                      {/* Indicatore sottomultiplo auto-rilevato */}
                      {newBooking.submultiple_guests && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-xs text-blue-700 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Sottomultiplo rilevato dal codice: <strong>{newBooking.submultiple_guests} ospiti</strong>
                          </p>
                        </div>
                      )}
                      
                      {/* Dropdown selezione manuale sottomultipli */}
                      {newBooking.property_id && (() => {
                        const selectedProperty = properties.find(p => p.id === newBooking.property_id);
                        if (selectedProperty && selectedProperty.apply_submultiples && selectedProperty.available_submultiples && selectedProperty.available_submultiples.length > 0) {
                          return (
                            <div className="mt-3">
                              <Label className="text-sm text-slate-600">Selezione Sottomultiplo (opzionale)</Label>
                              <Select
                                value={newBooking.submultiple_guests ? newBooking.submultiple_guests.toString() : 'full'}
                                onValueChange={(value) => {
                                  if (value === 'full') {
                                    setNewBooking({
                                      ...newBooking, 
                                      submultiple_guests: null,
                                      guests_count: selectedProperty.max_guests
                                    });
                                  } else {
                                    const submultipleNum = parseInt(value);
                                    setNewBooking({
                                      ...newBooking, 
                                      submultiple_guests: submultipleNum,
                                      guests_count: submultipleNum
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Seleziona capacità" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="full">
                                    Capacità Completa ({selectedProperty.max_guests} ospiti)
                                  </SelectItem>
                                  {selectedProperty.available_submultiples.sort((a, b) => b - a).map(sub => (
                                    <SelectItem key={sub} value={sub.toString()}>
                                      Sottomultiplo: {sub} ospiti
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-slate-500 mt-1">
                                💡 Seleziona manualmente se vuoi modificare il sottomultiplo rilevato
                              </p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  {/* Booking Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-3 block">Tipo Prenotazione *</Label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="refundable"
                            checked={newBooking.booking_type === 'refundable'}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setNewBooking({...newBooking, booking_type: 'refundable'});
                              }
                            }}
                          />
                          <Label htmlFor="refundable" className="text-sm cursor-pointer">
                            Rimborsabile (R)
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="non-refundable"
                            checked={newBooking.booking_type === 'non_refundable'}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setNewBooking({...newBooking, booking_type: 'non_refundable'});
                              }
                            }}
                          />
                          <Label htmlFor="non-refundable" className="text-sm cursor-pointer">
                            Non Rimborsabile (N)
                          </Label>
                        </div>
                        {newBooking.booking_code && (newBooking.booking_code.toUpperCase().includes('R') || newBooking.booking_code.toUpperCase().includes('N')) && (
                          <p className="text-xs text-blue-600 mt-2">
                            ✓ Auto-rilevato dal codice
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label>Stato Pagamento</Label>
                      <Select 
                        value={newBooking.payment_status}
                        onValueChange={(value) => setNewBooking({...newBooking, payment_status: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="collected">Incassato</SelectItem>
                          <SelectItem value="to_collect">Da Incassare</SelectItem>
                          <SelectItem value="partial">Acconto (Saldo in sospeso)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      {/* Placeholder for better grid layout */}
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Data Arrivo Prenotazione *</Label>
                      <Input
                        type="date"
                        value={newBooking.booking_date}
                        onChange={(e) => setNewBooking({...newBooking, booking_date: e.target.value})}
                        required
                        data-testid="booking-date-input"
                      />
                    </div>
                    <div>
                      <Label>Check-in *</Label>
                      <Input
                        type="date"
                        value={newBooking.check_in}
                        onChange={(e) => setNewBooking({...newBooking, check_in: e.target.value})}
                        required
                        data-testid="checkin-input"
                      />
                    </div>
                    <div>
                      <Label>Check-out *</Label>
                      <Input
                        type="date"
                        value={newBooking.check_out}
                        onChange={(e) => setNewBooking({...newBooking, check_out: e.target.value})}
                        min={newBooking.check_in ? (() => {
                          const minDate = new Date(newBooking.check_in);
                          minDate.setDate(minDate.getDate() + 1);
                          return minDate.toISOString().split('T')[0];
                        })() : ''}
                        required
                        data-testid="checkout-input"
                      />
                    </div>
                    {newBooking.payment_status === 'partial' && (
                      <div>
                        <Label>Data Saldo</Label>
                        <Input
                          type="date"
                          value={newBooking.balance_due_date}
                          onChange={(e) => setNewBooking({...newBooking, balance_due_date: e.target.value})}
                          data-testid="balance-date-input"
                        />
                      </div>
                    )}
                  </div>

                  {/* Pricing */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Prezzo Totale Ospite (€) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newBooking.guest_total}
                        onChange={(e) => setNewBooking({...newBooking, guest_total: e.target.value})}
                        required
                        data-testid="guest-total-input"
                      />
                    </div>
                    <div>
                      <Label>Prezzo al Proprietario (€) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newBooking.owner_amount}
                        onChange={(e) => setNewBooking({...newBooking, owner_amount: e.target.value})}
                        required
                        data-testid="owner-amount-input"
                      />
                    </div>
                    <div>
                      <Label>Tassa di Soggiorno (€) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newBooking.tourist_tax}
                        onChange={(e) => setNewBooking({...newBooking, tourist_tax: e.target.value})}
                        required
                        data-testid="tourist-tax-input"
                      />
                    </div>
                    <div>
                      <Label>Costo Pulizie (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newBooking.cleaning_cost}
                        onChange={(e) => setNewBooking({...newBooking, cleaning_cost: e.target.value})}
                        className="bg-white"
                        data-testid="cleaning-cost-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {newBooking.property_id 
                          ? "Pre-compilato dall'immobile (modificabile)" 
                          : "Inserisci il costo di pulizia"
                        }
                      </p>
                    </div>
                    
                    <div>
                      <Label>Totale Biancheria (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newBooking.linen_cost}
                        onChange={(e) => setNewBooking({...newBooking, linen_cost: parseFloat(e.target.value) || 0})}
                        className="bg-white"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {newBooking.linen_cost_per_person > 0 && newBooking.property_id
                          ? `Auto-calcolato: €${newBooking.linen_cost_per_person.toFixed(2)} × ${newBooking.guests_count} ospiti (modificabile)`
                          : "Pre-compilato dall'immobile (modificabile)"
                        }
                      </p>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Pagamento Ospite</Label>
                      <Select 
                        value={newBooking.guest_payment_method}
                        onValueChange={(value) => setNewBooking({...newBooking, guest_payment_method: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="card">Carta (costo 5%)</SelectItem>
                          <SelectItem value="bank_transfer">Bonifico</SelectItem>
                          <SelectItem value="on_site">Sul Posto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Pagamento Tassa Soggiorno</Label>
                      <Select 
                        value={newBooking.tourist_tax_payment}
                        onValueChange={(value) => setNewBooking({...newBooking, tourist_tax_payment: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="card">Carta</SelectItem>
                          <SelectItem value="bank_transfer">Bonifico</SelectItem>
                          <SelectItem value="on_site">Sul Posto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Pagamento Pulizie</Label>
                      <Select 
                        value={newBooking.cleaning_payment}
                        onValueChange={(value) => setNewBooking({...newBooking, cleaning_payment: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="card">Carta</SelectItem>
                          <SelectItem value="bank_transfer">Bonifico</SelectItem>
                          <SelectItem value="on_site">Sul Posto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Margin Warning */}
                  {marginWarning && (
                    <Card className="p-4 border-red-200 bg-red-50">
                      <div className="flex items-start space-x-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-red-800 font-semibold">Margine Negativo!</h4>
                          <p className="text-red-700 text-sm mt-1">{marginWarning.message}</p>
                          <div className="mt-2 text-xs text-red-600 space-y-1">
                            <div>Costo carta: €{marginWarning.card_cost?.toFixed(2)}</div>
                            <div>Commissione piattaforma: €{marginWarning.platform_commission?.toFixed(2)}</div>
                            <div>Commissione partner: €{marginWarning.partner_commission?.toFixed(2)}</div>
                          </div>
                          <div className="mt-3 space-x-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setMarginWarning(null)}
                            >
                              Correggi Dati
                            </Button>
                            <Button
                              type="submit"
                              size="sm"
                              className="bg-red-600 hover:bg-red-700"
                              onClick={(e) => handleCreateBooking(e, true)}
                            >
                              Salva Comunque
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}

                  <div className="flex justify-between pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={calculateMargin}
                      className="flex items-center space-x-2"
                    >
                      <Calculator className="h-4 w-4" />
                      <span>Calcola Margine</span>
                    </Button>
                    
                    <div className="space-x-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setShowNewBooking(false)}
                      >
                        Annulla
                      </Button>
                      <Button 
                        type="submit"
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid="save-booking-btn"
                        disabled={marginWarning && marginWarning.is_negative}
                      >
                        Crea Prenotazione
                      </Button>
                    </div>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>

            {/* Dialog for adding new property */}
            <Dialog open={showNewProperty} onOpenChange={setShowNewProperty}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center space-x-2">
                    <Building className="h-5 w-5" />
                    <span>Aggiungi Nuovo Immobile</span>
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome Immobile *</Label>
                      <Input
                        value={newProperty.name}
                        onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                        required
                        placeholder="Villa del Mare"
                        data-testid="new-property-name"
                      />
                    </div>
                    <div>
                      <Label>Città *</Label>
                      <Input
                        value={newProperty.city}
                        onChange={(e) => setNewProperty({...newProperty, city: e.target.value})}
                        required
                        placeholder="Milano"
                        data-testid="new-property-city"
                      />
                    </div>
                    <div>
                      <Label>Indirizzo *</Label>
                      <Input
                        value={newProperty.address}
                        onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                        required
                        placeholder="Via Roma 123"
                        data-testid="new-property-address"
                      />
                    </div>
                    <div>
                      <Label>Regione *</Label>
                      <Select 
                        value={newProperty.region}
                        onValueChange={(value) => setNewProperty({...newProperty, region: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona regione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lombardia">Lombardia</SelectItem>
                          <SelectItem value="veneto">Veneto</SelectItem>
                          <SelectItem value="toscana">Toscana</SelectItem>
                          <SelectItem value="lazio">Lazio</SelectItem>
                          <SelectItem value="campania">Campania</SelectItem>
                          <SelectItem value="sicilia">Sicilia</SelectItem>
                          <SelectItem value="emilia-romagna">Emilia-Romagna</SelectItem>
                          <SelectItem value="piemonte">Piemonte</SelectItem>
                          <SelectItem value="puglia">Puglia</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>CAP</Label>
                      <Input
                        value={newProperty.postal_code}
                        onChange={(e) => setNewProperty({...newProperty, postal_code: e.target.value})}
                        placeholder="20100"
                      />
                    </div>
                    <div>
                      <Label>Tipo Immobile</Label>
                      <Select 
                        value={newProperty.property_type}
                        onValueChange={(value) => setNewProperty({...newProperty, property_type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="apartment">Appartamento</SelectItem>
                          <SelectItem value="villa">Villa</SelectItem>
                          <SelectItem value="house">Casa</SelectItem>
                          <SelectItem value="studio">Monolocale</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Camere</Label>
                      <Input
                        type="number"
                        min="0"
                        value={newProperty.bedrooms}
                        onChange={(e) => setNewProperty({...newProperty, bedrooms: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Bagni</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newProperty.bathrooms}
                        onChange={(e) => setNewProperty({...newProperty, bathrooms: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Max Ospiti</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newProperty.max_guests}
                        onChange={(e) => setNewProperty({...newProperty, max_guests: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Prezzo/Notte (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newProperty.price_per_night}
                        onChange={(e) => setNewProperty({...newProperty, price_per_night: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Costo Pulizie (€) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newProperty.cleaning_cost}
                        onChange={(e) => setNewProperty({...newProperty, cleaning_cost: e.target.value})}
                        required
                        placeholder="50.00"
                      />
                    </div>
                    <div>
                      <Label>Email Responsabile</Label>
                      <Input
                        type="email"
                        value={newProperty.responsible_email}
                        onChange={(e) => setNewProperty({...newProperty, responsible_email: e.target.value})}
                        placeholder="responsabile@example.com"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNewProperty(false)}
                    >
                      Annulla
                    </Button>
                    <Button 
                      type="button"
                      onClick={handleCreateProperty}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="save-new-property-btn"
                    >
                      Crea e Seleziona Immobile
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </Card>

        {/* Tabs for Active/Cancelled bookings */}
        <Card className="p-0 border-0 shadow-lg overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 px-6 py-4 font-semibold transition-all ${
                activeTab === 'active'
                  ? 'bg-blue-900 text-white border-b-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-blue-900'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Calendar className="h-5 w-5" />
                <span>Prenotazioni Attive</span>
                <Badge variant="secondary" className="bg-blue-100 text-blue-900">
                  {activeTab === 'active' ? filteredBookings.length : bookings.filter(b => !b.cancelled).length}
                </Badge>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('cancelled')}
              className={`flex-1 px-6 py-4 font-semibold transition-all ${
                activeTab === 'cancelled'
                  ? 'bg-blue-900 text-white border-b-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-blue-900'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <XCircle className="h-5 w-5" />
                <span>Prenotazioni Annullate</span>
                <Badge variant="secondary" className="bg-red-100 text-red-900">
                  {activeTab === 'cancelled' ? filteredBookings.length : bookings.filter(b => b.cancelled).length}
                </Badge>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('owner-payments')}
              className={`flex-1 px-6 py-4 font-semibold transition-all ${
                activeTab === 'owner-payments'
                  ? 'bg-blue-900 text-white border-b-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-blue-900'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Euro className="h-5 w-5" />
                <span>Proprietari da Pagare</span>
                <Badge variant="secondary" className="bg-green-100 text-green-900">
                  {activeTab === 'owner-payments' ? filteredBookings.length : 0}
                </Badge>
              </div>
            </button>
          </div>
        </Card>

        {/* Statistiche Giornata - compact */}
        {showTodayFilter && (
          <Card className="p-2 border-l-4 border-l-green-500 shadow-sm">
            <div className="flex items-center justify-between px-2">
              <span className="text-sm font-medium text-green-800">Prenotazioni di Oggi: <strong>{todayStats.count}</strong></span>
              <span className="text-sm font-bold text-green-800">€{todayStats.total.toFixed(2)}</span>
            </div>
          </Card>
        )}

        {/* Year + Filters - compact */}
        <Card className="p-3 border-0 shadow-sm">
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-1">
              <YearSelector currentYear={currentYear} onChange={setCurrentYear} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 items-end">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-slate-400 h-3.5 w-3.5" />
                <Input
                  placeholder="Codice..."
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                  className="pl-8 uppercase font-mono text-xs h-8"
                  data-testid="search-code-input"
                />
              </div>
              
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-slate-400 h-3.5 w-3.5" />
                <Input
                  placeholder="Ospite..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 text-xs h-8"
                  data-testid="search-guest-input"
                />
              </div>
              
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-slate-400 h-3.5 w-3.5" />
                <Input
                  placeholder="Immobile..."
                  value={searchProperty}
                  onChange={(e) => setSearchProperty(e.target.value)}
                  className="pl-8 text-xs h-8"
                  data-testid="search-property-input"
                />
              </div>
              
              <Select value={filters.platform} onValueChange={(value) => setFilters({...filters, platform: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Piattaforma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le piattaforme</SelectItem>
                  <SelectItem value="holiday_world">Holiday World</SelectItem>
                  <SelectItem value="airbnb">Airbnb</SelectItem>
                  <SelectItem value="booking_com">Booking.com</SelectItem>
                  <SelectItem value="atraveo">Atraveo</SelectItem>
                  <SelectItem value="housinganywhere">Housinganywhere</SelectItem>
                  <SelectItem value="spotahome">Spotahome</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.booking_type} onValueChange={(value) => setFilters({...filters, booking_type: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="refundable">Rimborsabile</SelectItem>
                  <SelectItem value="non_refundable">Non Rimborsabile</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.payment_status} onValueChange={(value) => setFilters({...filters, payment_status: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Pagamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i pagamenti</SelectItem>
                  <SelectItem value="collected">Incassato</SelectItem>
                  <SelectItem value="to_collect">Da Incassare</SelectItem>
                  <SelectItem value="partial">Parziale</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.incomplete} onValueChange={(value) => setFilters({...filters, incomplete: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Completezza" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="incomplete">⚠️ Incomplete</SelectItem>
                  <SelectItem value="complete">✅ Complete</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilters({
                    platform: 'all',
                    booking_type: 'all',
                    payment_status: 'all',
                    date_range: 'all',
                    date_type: 'booking_date',
                    date_from: '',
                    date_to: '',
                    month: '',
                    incomplete: 'all'
                  });
                  setSearchTerm('');
                  setSearchCode('');
                  setSearchProperty('');
                }}
                className="w-full"
              >
                Cancella Filtri
              </Button>
            </div>

            {/* Filtri per data - compact */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-2 bg-blue-50 rounded border border-blue-200 items-end">
              <div className="flex items-center space-x-1">
                <Calendar className="h-3.5 w-3.5 text-blue-600" />
                <Label className="text-xs font-semibold text-blue-900">Data:</Label>
              </div>
              
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Tipo Data</Label>
                <Select value={filters.date_type} onValueChange={(value) => setFilters({...filters, date_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tipo Data" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="booking_date">Data Prenotazione</SelectItem>
                    <SelectItem value="check_in">Check-in</SelectItem>
                    <SelectItem value="check_out">Check-out</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Mese</Label>
                <Input
                  type="month"
                  value={filters.month}
                  onChange={(e) => handleMonthChange(e.target.value)}
                  className="w-full"
                  placeholder="Seleziona mese"
                />
              </div>

              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Da</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value, month: ''})}
                  className="w-full"
                />
              </div>

              <div>
                <Label className="text-xs text-slate-600 mb-1 block">A</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({...filters, date_to: e.target.value, month: ''})}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Bookings table */}
        <div className="space-y-0">
          <Card className="p-0 border-0 shadow-lg overflow-hidden rounded-b-none">
            {filteredBookings.length === 0 ? (
              <div className="p-8 text-center">
                <Calendar className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                <h3 className="text-lg font-semibold text-slate-600 mb-2">Nessuna prenotazione trovata</h3>
                <p className="text-slate-500">
                  {searchCode || searchTerm || searchProperty || Object.values(filters).some(f => f !== 'all')
                    ? 'Prova a modificare i filtri di ricerca' 
                    : 'Inizia creando la prima prenotazione'
                  }
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto" style={{maxHeight: 'calc(100vh - 340px)', overflowY: 'auto'}}>
                <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-40 shadow-sm">
                  <tr className="text-left bg-slate-100">
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('property_id')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Alloggio</span>
                        {getSortIcon('property_id')}
                      </button>
                    </th>
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('booking_code')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Codice</span>
                        {getSortIcon('booking_code')}
                      </button>
                    </th>
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('guest_name')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Ospite</span>
                        {getSortIcon('guest_name')}
                      </button>
                    </th>
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('booking_type')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Tipo</span>
                        {getSortIcon('booking_type')}
                      </button>
                    </th>
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('platform')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Piattaforma</span>
                        {getSortIcon('platform')}
                      </button>
                    </th>
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('booking_date')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Data Prenotazione</span>
                        {getSortIcon('booking_date')}
                      </button>
                    </th>
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('check_in')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Check-in</span>
                        {getSortIcon('check_in')}
                      </button>
                    </th>
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('check_out')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Check-out</span>
                        {getSortIcon('check_out')}
                      </button>
                    </th>
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('guest_total')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Totale Ospite</span>
                        {getSortIcon('guest_total')}
                      </button>
                    </th>
                    <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                      <button 
                        onClick={() => handleSort('payment_status')}
                        className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                      >
                        <span>Stato Pag.</span>
                        {getSortIcon('payment_status')}
                      </button>
                    </th>
                    {user?.role === 'admin' && (
                      <>
                        <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                          <button 
                            onClick={() => handleSort('owner_amount')}
                            className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                          >
                            <span>Proprietario</span>
                            {getSortIcon('owner_amount')}
                          </button>
                        </th>
                        <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                          <button 
                            onClick={() => handleSort('tourist_tax')}
                            className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                          >
                            <span>Tassa Sogg.</span>
                            {getSortIcon('tourist_tax')}
                          </button>
                        </th>
                        <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                          <button 
                            onClick={() => handleSort('cleaning_cost')}
                            className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                          >
                            <span>Pulizie</span>
                            {getSortIcon('cleaning_cost')}
                          </button>
                        </th>
                        <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                          <button 
                            onClick={() => handleSort('card_cost')}
                            className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                          >
                            <span>Costo Carta</span>
                            {getSortIcon('card_cost')}
                          </button>
                        </th>
                        <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                          <button 
                            onClick={() => handleSort('platform_commission')}
                            className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                          >
                            <span>Comm. Piatt.</span>
                            {getSortIcon('platform_commission')}
                          </button>
                        </th>
                        <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                          <button 
                            onClick={() => handleSort('partner_commission')}
                            className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                          >
                            <span>Comm. Partner</span>
                            {getSortIcon('partner_commission')}
                          </button>
                        </th>
                        <th className="p-3 font-semibold text-slate-800 bg-slate-100 border-r border-slate-200">
                          <button 
                            onClick={() => handleSort('margin')}
                            className="flex items-center space-x-1 hover:text-blue-600 font-bold"
                          >
                            <span>Margine</span>
                            {getSortIcon('margin')}
                          </button>
                        </th>
                      </>
                    )}
                    
                    {/* Actions column - only for admin and bookings_operator */}
                    {(user?.role === 'admin' || user?.role === 'bookings_operator') && (
                      <th className="p-3 font-semibold text-slate-800 bg-slate-100">
                        Azioni
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => {
                    const property = properties.find(p => p.id === booking.property_id);
                    const displayName = property?.name || booking.property_name || 'Non associato';
                    const displayCity = property?.city || '';
                    return (
                      <tr key={booking.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`booking-row-${booking.id}`}>
                        <td className="p-3">
                          <div className={`font-medium text-sm ${property ? 'text-slate-900' : 'text-amber-600'}`}>{displayName}</div>
                          {displayCity && <div className="text-xs text-slate-500">{displayCity}</div>}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <div className="font-mono text-sm text-blue-900">{booking.booking_code}</div>
                            {booking.is_incomplete && (
                              <span title={`Campi mancanti: ${booking.missing_fields?.join(', ') || 'N/A'}`} className="text-amber-500">
                                <AlertTriangle className="w-4 h-4" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-slate-900 text-sm">{booking.guest_name}</div>
                          {booking.guest_phone && (
                            <div className="text-xs text-slate-500">{booking.guest_phone}</div>
                          )}
                          <div className="text-xs text-slate-500">{booking.guests_count} ospiti</div>
                        </td>
                        <td className="p-3">
                          <Badge variant={booking.booking_type === 'refundable' ? 'default' : 'secondary'} className="text-xs">
                            {booking.booking_type === 'refundable' ? 'Rimb.' : 'Non Rimb.'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="text-xs">{getPlatformLabel(booking.platform)}</div>
                        </td>
                        <td className="p-3">
                          <div className="text-xs font-medium text-blue-600">{new Date(booking.booking_date).toLocaleDateString('it-IT')}</div>
                        </td>
                        <td className="p-3">
                          <div className="text-xs">{new Date(booking.check_in).toLocaleDateString('it-IT')}</div>
                        </td>
                        <td className="p-3">
                          <div className="text-xs">{new Date(booking.check_out).toLocaleDateString('it-IT')}</div>
                        </td>
                        <td className="p-3">
                          <div className="font-semibold text-slate-900 text-sm">€{booking.guest_total?.toLocaleString('it-IT')}</div>
                          <div className="text-xs text-slate-500">{booking.guest_payment_method}</div>
                        </td>
                        <td className="p-3">
                          {getPaymentStatusBadge(booking.payment_status)}
                        </td>
                        {user?.role === 'admin' && (
                          <>
                            <td className="p-3">
                              <div className="font-medium text-slate-900 text-sm">€{booking.owner_amount?.toFixed(2)}</div>
                            </td>
                            <td className="p-3">
                              <div className="font-medium text-slate-900 text-sm">€{booking.tourist_tax?.toFixed(2)}</div>
                              <div className="text-xs text-slate-500">{booking.tourist_tax_payment}</div>
                            </td>
                            <td className="p-3">
                              <div className="font-medium text-slate-900 text-sm">€{booking.cleaning_cost?.toFixed(2)}</div>
                              <div className="text-xs text-slate-500">{booking.cleaning_payment}</div>
                            </td>
                            <td className="p-3">
                              <div className="text-sm text-orange-600">€{booking.card_cost?.toFixed(2)}</div>
                            </td>
                            <td className="p-3">
                              <div className="text-sm text-purple-600">€{booking.platform_commission?.toFixed(2)}</div>
                            </td>
                            <td className="p-3">
                              <div className="text-sm text-blue-600">€{booking.partner_commission?.toFixed(2)}</div>
                            </td>
                            <td className="p-3 border-r border-slate-200">
                              <div className={`font-bold text-sm ${booking.margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                €{booking.margin?.toFixed(2)}
                              </div>
                            </td>
                          </>
                        )}
                        
                        {/* Actions column */}
                        {(user?.role === 'admin' || user?.role === 'bookings_operator') && (
                          <td className="p-3">
                            <div className="flex flex-col gap-2">
                              {activeTab === 'active' ? (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditBooking(booking)}
                                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                                  >
                                    <Edit className="h-4 w-4 mr-1" />
                                    Modifica
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setCancelModal({ show: true, booking })}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Annulla
                                  </Button>
                                  {/* Pulsante Questura per Admin */}
                                  {user?.role === 'admin' && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={async () => {
                                        if (!window.confirm(
                                          booking.questura_sent 
                                            ? 'Questa prenotazione è già stata inviata. Vuoi forzare un nuovo invio?' 
                                            : 'Inviare i dati alla Questura?'
                                        )) return;
                                        try {
                                          const token = localStorage.getItem('hw_token');
                                          toast.info('Invio in corso...');
                                          const response = await axios.post(
                                            `${API}/api/alloggiati/submit/${booking.id}`,
                                            {},
                                            { headers: { Authorization: `Bearer ${token}` } }
                                          );
                                          if (response.data.success) {
                                            const receiptInfo = response.data.receipt_number ? `\nRicevuta: ${response.data.receipt_number}` : '';
                                            setQuesturaPopup({ open: true, title: 'Invio avvenuto correttamente!', message: `Dati per ${booking.guest_name} confermati dalla Questura.${receiptInfo}`, type: 'success', details: [] });
                                            fetchBookings();
                                          } else {
                                            const errorDetails = [];
                                            if (response.data.validation_errors) errorDetails.push(...response.data.validation_errors);
                                            if (response.data.errors && response.data.errors.length > 0) errorDetails.push(...response.data.errors);
                                            if (response.data.error_detail) errorDetails.push(response.data.error_detail);
                                            setQuesturaPopup({ open: true, title: 'Errore invio Questura', message: response.data.error || response.data.message || 'Errore durante l\'invio', type: 'error', details: errorDetails });
                                          }
                                        } catch (err) {
                                          const errData = err.response?.data;
                                          const errorDetails = [];
                                          if (errData?.validation_errors) errorDetails.push(...errData.validation_errors);
                                          if (errData?.errors && errData.errors.length > 0) errorDetails.push(...errData.errors);
                                          if (errData?.error_detail) errorDetails.push(errData.error_detail);
                                          setQuesturaPopup({ open: true, title: 'Errore invio Questura', message: errData?.error || errData?.message || 'Errore di connessione. Verifica i dati e riprova.', type: 'error', details: errorDetails });
                                        }
                                      }}
                                      className={booking.questura_sent 
                                        ? 'border-amber-500 text-amber-600 hover:bg-amber-50' 
                                        : 'border-purple-500 text-purple-600 hover:bg-purple-50'
                                      }
                                    >
                                      <Send className="h-4 w-4 mr-1" />
                                      {booking.questura_sent ? 'Forza Questura' : 'Questura'}
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setRestoreModal({ show: true, booking })}
                                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                                  >
                                    <RotateCcw className="h-4 w-4 mr-1" />
                                    Ripristina
                                  </Button>
                                  <div className="text-xs text-gray-500">
                                    <div>Annullata: {new Date(booking.cancelled_date).toLocaleDateString('it-IT')}</div>
                                    <div>Da: {booking.cancelled_by}</div>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  
                  {/* Rimuoviamo la riga totali dalla tabella per metterla come barra fissa */}
                </tbody>
              </table>
              </div>
            )}

              
              {/* OWNER PAYMENTS TAB */}
              {activeTab === 'owner-payments' && (
                <div className="overflow-x-auto" style={{maxHeight: 'calc(100vh - 340px)', overflowY: 'auto'}}>
                  {/* Filter by owner payment status */}
                  <div className="mb-4 flex items-center gap-4 bg-slate-50 p-4 rounded-lg">
                    <span className="font-medium text-slate-700">Filtra per Stato:</span>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setOwnerPaymentFilter('all')}
                        variant={ownerPaymentFilter === 'all' ? 'default' : 'outline'}
                        size="sm"
                      >
                        Tutti
                      </Button>
                      <Button
                        onClick={() => setOwnerPaymentFilter('confermata')}
                        variant={ownerPaymentFilter === 'confermata' ? 'default' : 'outline'}
                        size="sm"
                        className={ownerPaymentFilter === 'confermata' ? 'bg-blue-600' : ''}
                      >
                        🟢 Confermate
                      </Button>
                      <Button
                        onClick={() => setOwnerPaymentFilter('da_pagare')}
                        variant={ownerPaymentFilter === 'da_pagare' ? 'default' : 'outline'}
                        size="sm"
                        className={ownerPaymentFilter === 'da_pagare' ? 'bg-yellow-600' : ''}
                      >
                        🟡 Da Pagare
                      </Button>
                      <Button
                        onClick={() => setOwnerPaymentFilter('pagato')}
                        variant={ownerPaymentFilter === 'pagato' ? 'default' : 'outline'}
                        size="sm"
                        className={ownerPaymentFilter === 'pagato' ? 'bg-green-600' : ''}
                      >
                        ✅ Pagate
                      </Button>
                    </div>
                  </div>

                  {loadingOwnerPayments ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
                    </div>
                  ) : ownerPayments.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      Nessuna prenotazione trovata per il filtro selezionato
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                        <tr className="text-left bg-slate-100">
                          <th className="p-3 font-semibold text-slate-800 border-r border-slate-200">
                            Nome Casa
                          </th>
                          <th className="p-3 font-semibold text-slate-800 border-r border-slate-200">
                            Codice
                          </th>
                          <th className="p-3 font-semibold text-slate-800 border-r border-slate-200">
                            Ospite
                          </th>
                          <th className="p-3 font-semibold text-slate-800 border-r border-slate-200">
                            Check-in
                          </th>
                          <th className="p-3 font-semibold text-slate-800 border-r border-slate-200">
                            Data Pagamento
                            <div className="text-xs font-normal text-slate-500">(+3 gg lavorativi)</div>
                          </th>
                          <th className="p-3 font-semibold text-slate-800 border-r border-slate-200">
                            Totale Proprietario
                          </th>
                          <th className="p-3 font-semibold text-slate-800 border-r border-slate-200">
                            Stato
                          </th>
                          <th className="p-3 font-semibold text-slate-800">
                            Azioni
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {ownerPayments.map((booking) => {
                          const property = properties.find(p => p.id === booking.property_id);
                          const statusBadge = {
                            'confermata': { color: 'bg-blue-100 text-blue-800', icon: '🟢', label: 'Confermata' },
                            'da_pagare': { color: 'bg-yellow-100 text-yellow-800', icon: '🟡', label: 'Da Pagare' },
                            'pagato': { color: 'bg-green-100 text-green-800', icon: '✅', label: 'Pagato' }
                          }[booking.owner_payment_status] || { color: 'bg-gray-100 text-gray-800', icon: '⚪', label: 'N/A' };

                          return (
                            <tr key={booking.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="p-3">
                                <div className={`font-medium text-sm ${property ? 'text-slate-900' : 'text-amber-600'}`}>
                                  {property?.name || booking.property_name || 'Non associato'}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="text-sm text-slate-700">{booking.booking_code}</div>
                              </td>
                              <td className="p-3">
                                <div className="text-sm text-slate-700">{booking.guest_name}</div>
                              </td>
                              <td className="p-3">
                                <div className="text-sm text-slate-700">
                                  {new Date(booking.check_in).toLocaleDateString('it-IT')}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="text-sm text-slate-700">
                                  {booking.owner_payment_due_date 
                                    ? new Date(booking.owner_payment_due_date).toLocaleDateString('it-IT')
                                    : 'N/A'
                                  }
                                </div>
                                {booking.owner_payment_date && (
                                  <div className="text-xs text-green-600">
                                    Pagato: {new Date(booking.owner_payment_date).toLocaleDateString('it-IT')}
                                  </div>
                                )}
                              </td>
                              <td className="p-3">
                                <div className="font-semibold text-slate-900">
                                  €{booking.owner_amount?.toFixed(2) || '0.00'}
                                </div>
                              </td>
                              <td className="p-3">
                                <Badge className={`${statusBadge.color} px-2 py-1`}>
                                  {statusBadge.icon} {statusBadge.label}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <div className="flex gap-1">
                                  {booking.owner_payment_status !== 'confermata' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => updateOwnerPaymentStatus(booking.id, 'confermata')}
                                      className="text-xs"
                                      title="Segna come Confermata"
                                    >
                                      🟢
                                    </Button>
                                  )}
                                  {booking.owner_payment_status !== 'da_pagare' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => updateOwnerPaymentStatus(booking.id, 'da_pagare')}
                                      className="text-xs"
                                      title="Segna come Da Pagare"
                                    >
                                      🟡
                                    </Button>
                                  )}
                                  {booking.owner_payment_status !== 'pagato' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => updateOwnerPaymentStatus(booking.id, 'pagato')}
                                      className="text-xs bg-green-50"
                                      title="Segna come Pagato"
                                    >
                                      ✅
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

          </Card>
          
          {/* Rimoviamo la barra vecchia dato che ora è fissa in alto */}
        </div>
      </div>
      {/* Edit Booking Modal */}
      <Dialog open={editModal.show} onOpenChange={(open) => setEditModal({ show: open, booking: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Prenotazione</DialogTitle>
          </DialogHeader>
          {editBookingData && (
            <div className="space-y-4">
              {/* Banner Modifiche Manuali Protette */}
              {(editBookingData.manually_edited_fields || []).length > 0 && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="manual-fields-banner">
                  <Lock className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="font-semibold text-amber-900">
                      {editBookingData.manually_edited_fields.length} {editBookingData.manually_edited_fields.length === 1 ? 'campo modificato manualmente' : 'campi modificati manualmente'}
                    </p>
                    <p className="text-amber-800 text-xs mt-0.5">
                      I campi con <Lock className="inline h-3 w-3" /> sono <strong>protetti dal sync Booking.com/HW</strong>: non verranno sovrascritti dalle prossime importazioni.
                      {editBookingData.last_manual_edit_at && (
                        <> · Ultima modifica: {new Date(editBookingData.last_manual_edit_at).toLocaleString('it-IT')}</>
                      )}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleResetManualFields(null)}
                    className="border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0"
                    data-testid="manual-reset-all-btn"
                  >
                    <Unlock className="h-3.5 w-3.5 mr-1" /> Riprendi tutto da portale
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {/* Basic Info */}
                <div className="col-span-2">
                  <Label>Codice Prenotazione *</Label>
                  <Input 
                    value={editBookingData.booking_code}
                    onChange={(e) => {
                      const newCode = e.target.value;
                      const codeUpper = newCode.toUpperCase();
                      
                      // Auto-detect booking type from code
                      let autoBookingType = editBookingData.booking_type;
                      if (codeUpper.includes('N')) {
                        autoBookingType = 'non_refundable';
                      } else if (codeUpper.includes('R')) {
                        autoBookingType = 'refundable';
                      }
                      
                      setEditBookingData({...editBookingData, booking_code: newCode, booking_type: autoBookingType});
                      checkPropertyMatch(newCode);
                    }}
                    placeholder="HMTX88IY7W"
                  />
                  {suggestedProperty && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                      <p className="text-blue-900">
                        ✓ Sarà associato automaticamente a: <strong>{suggestedProperty.name}</strong> ({suggestedProperty.property_code})
                      </p>
                    </div>
                  )}
                  {!suggestedProperty && editBookingData.booking_code.length >= 4 && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                      <p className="text-yellow-900">
                        ⚠ Nessun immobile trovato per prefisso del codice <code className="bg-white px-1 rounded">{editBookingData.booking_code.substring(0, Math.min(editBookingData.booking_code.length, 12))}</code> (provati prefissi 12→4 caratteri)
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Modificabile - L'immobile viene associato automaticamente</p>
                </div>
                <div>
                  <LockableLabel field="guest_name">Nome Ospite</LockableLabel>
                  <Input
                    value={editBookingData.guest_name}
                    onChange={(e) => setEditBookingData({...editBookingData, guest_name: e.target.value})}
                  />
                </div>
                <div>
                  <LockableLabel field="guest_phone">Telefono Ospite</LockableLabel>
                  <Input
                    type="tel"
                    value={editBookingData.guest_phone || ''}
                    onChange={(e) => setEditBookingData({...editBookingData, guest_phone: e.target.value})}
                    placeholder="+39 123 456 7890"
                    data-testid="edit-guest-phone-input"
                  />
                </div>
                <div>
                  <LockableLabel field="guest_email">Email Ospite</LockableLabel>
                  <Input
                    type="email"
                    value={editBookingData.guest_email || ''}
                    onChange={(e) => setEditBookingData({...editBookingData, guest_email: e.target.value})}
                    placeholder="ospite@email.com"
                    data-testid="edit-guest-email-input"
                  />
                </div>
                <div>
                  <LockableLabel field="guest_nationality">Nazionalità Ospite</LockableLabel>
                  <select
                    value={editBookingData.guest_nationality || ''}
                    onChange={(e) => setEditBookingData({...editBookingData, guest_nationality: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="edit-guest-nationality-select"
                  >
                    <option value="">Seleziona nazionalità</option>
                    <option value="Italia">🇮🇹 Italia</option>
                    <option value="Francia">🇫🇷 Francia</option>
                    <option value="Germania">🇩🇪 Germania</option>
                    <option value="Regno Unito">🇬🇧 Regno Unito</option>
                    <option value="Spagna">🇪🇸 Spagna</option>
                    <option value="Stati Uniti">🇺🇸 Stati Uniti</option>
                    <option value="Svizzera">🇨🇭 Svizzera</option>
                    <option value="Austria">🇦🇹 Austria</option>
                    <option value="Paesi Bassi">🇳🇱 Paesi Bassi</option>
                    <option value="Belgio">🇧🇪 Belgio</option>
                    <option value="Polonia">🇵🇱 Polonia</option>
                    <option value="Russia">🇷🇺 Russia</option>
                    <option value="Cina">🇨🇳 Cina</option>
                    <option value="Giappone">🇯🇵 Giappone</option>
                    <option value="Brasile">🇧🇷 Brasile</option>
                    <option value="Argentina">🇦🇷 Argentina</option>
                    <option value="Canada">🇨🇦 Canada</option>
                    <option value="Australia">🇦🇺 Australia</option>
                    <option value="Altro">🌍 Altro</option>
                  </select>
                </div>

                {/* Dates */}
                <div>
                  <LockableLabel field="booking_date">Data Prenotazione</LockableLabel>
                  <Input
                    type="date"
                    value={editBookingData.booking_date}
                    onChange={(e) => setEditBookingData({...editBookingData, booking_date: e.target.value})}
                  />
                </div>
                <div>
                  <LockableLabel field="check_in">Check-in</LockableLabel>
                  <Input
                    type="date"
                    value={editBookingData.check_in}
                    onChange={(e) => setEditBookingData({...editBookingData, check_in: e.target.value})}
                  />
                </div>
                <div>
                  <LockableLabel field="check_out">Check-out</LockableLabel>
                  <Input
                    type="date"
                    value={editBookingData.check_out}
                    onChange={(e) => setEditBookingData({...editBookingData, check_out: e.target.value})}
                    min={editBookingData.check_in ? (() => {
                      const minDate = new Date(editBookingData.check_in);
                      minDate.setDate(minDate.getDate() + 1);
                      return minDate.toISOString().split('T')[0];
                    })() : ''}
                  />
                </div>
                <div>
                  <LockableLabel field="guests_count">Numero Ospiti</LockableLabel>
                  <Input
                    type="number"
                    value={editBookingData.guests_count}
                    onChange={(e) => setEditBookingData({...editBookingData, guests_count: e.target.value})}
                  />
                  
                  {/* Indicatore sottomultiplo */}
                  {editBookingData.submultiple_guests && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-xs text-blue-700 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Sottomultiplo attivo: <strong>{editBookingData.submultiple_guests} ospiti</strong>
                      </p>
                    </div>
                  )}
                  
                  {/* Dropdown selezione manuale sottomultipli */}
                  {editBookingData.property_id && (() => {
                    const selectedProperty = properties.find(p => p.id === editBookingData.property_id);
                    if (selectedProperty && selectedProperty.apply_submultiples && selectedProperty.available_submultiples && selectedProperty.available_submultiples.length > 0) {
                      return (
                        <div className="mt-3">
                          <Label className="text-sm text-slate-600">Selezione Sottomultiplo (opzionale)</Label>
                          <Select
                            value={editBookingData.submultiple_guests ? editBookingData.submultiple_guests.toString() : 'full'}
                            onValueChange={(value) => {
                              if (value === 'full') {
                                setEditBookingData({
                                  ...editBookingData, 
                                  submultiple_guests: null,
                                  guests_count: selectedProperty.max_guests
                                });
                              } else {
                                const submultipleNum = parseInt(value);
                                setEditBookingData({
                                  ...editBookingData, 
                                  submultiple_guests: submultipleNum,
                                  guests_count: submultipleNum
                                });
                              }
                            }}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Seleziona capacità" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="full">
                                Capacità Completa ({selectedProperty.max_guests} ospiti)
                              </SelectItem>
                              {selectedProperty.available_submultiples.sort((a, b) => b - a).map(sub => (
                                <SelectItem key={sub} value={sub.toString()}>
                                  Sottomultiplo: {sub} ospiti
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500 mt-1">
                            💡 Modifica manualmente il sottomultiplo se necessario
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Bambini (esenti da documenti) */}
                <div>
                  <LockableLabel field="children_count">Bambini (no documenti)</LockableLabel>
                  <Input
                    type="number"
                    min="0"
                    max={editBookingData.guests_count || 1}
                    value={editBookingData.children_count || 0}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      const maxVal = Math.max(0, Math.min(val, (editBookingData.guests_count || 1) - 1));
                      setEditBookingData({...editBookingData, children_count: maxVal});
                    }}
                    data-testid="children-count-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Bambini esenti da registrazione Questura
                  </p>
                  {editBookingData.children_count > 0 && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-xs text-amber-700">
                        👶 Adulti: <strong>{(editBookingData.guests_count || 1) - (editBookingData.children_count || 0)}</strong> | 
                        Bambini: <strong>{editBookingData.children_count}</strong>
                      </p>
                    </div>
                  )}
                </div>

                {/* Financial */}
                <div>
                  <LockableLabel field="guest_total">Totale Ospite (€)</LockableLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={editBookingData.guest_total}
                    onChange={(e) => setEditBookingData({...editBookingData, guest_total: e.target.value})}
                  />
                </div>
                <div>
                  <LockableLabel field="platform_commission">Commissione Piattaforma (€)</LockableLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={editBookingData.platform_commission}
                    onChange={(e) => setEditBookingData({...editBookingData, platform_commission: e.target.value})}
                  />
                </div>
                <div>
                  <LockableLabel field="partner_commission">Commissione Partner (€)</LockableLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={editBookingData.partner_commission}
                    onChange={(e) => setEditBookingData({...editBookingData, partner_commission: e.target.value})}
                  />
                </div>
                <div>
                  <LockableLabel field="cleaning_cost">Costo Pulizia (€)</LockableLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={editBookingData.cleaning_cost}
                    onChange={(e) => setEditBookingData({...editBookingData, cleaning_cost: e.target.value})}
                  />
                </div>

                {/* Platform & Type */}
                <div>
                  <LockableLabel field="platform">Piattaforma</LockableLabel>
                  <Select
                    value={editBookingData.platform}
                    onValueChange={(value) => setEditBookingData({...editBookingData, platform: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="airbnb">Airbnb</SelectItem>
                      <SelectItem value="booking">Booking.com</SelectItem>
                      <SelectItem value="interhome">Interhome</SelectItem>
                      <SelectItem value="atraveo">Atraveo</SelectItem>
                      <SelectItem value="holiday_world">Holiday World</SelectItem>
                      <SelectItem value="other">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <LockableLabel field="booking_type">Tipo Prenotazione</LockableLabel>
                  <Select
                    value={editBookingData.booking_type}
                    onValueChange={(value) => setEditBookingData({...editBookingData, booking_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="booking">Prenotazione</SelectItem>
                      <SelectItem value="inquiry">Richiesta</SelectItem>
                      <SelectItem value="alteration">Modifica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Payment Status */}
                <div>
                  <LockableLabel field="payment_status">Stato Pagamento</LockableLabel>
                  <Select
                    value={editBookingData.payment_status}
                    onValueChange={(value) => setEditBookingData({...editBookingData, payment_status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Pagato</SelectItem>
                      <SelectItem value="pending">In Attesa</SelectItem>
                      <SelectItem value="partial">Parziale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setEditModal({ show: false, booking: null })}
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleSaveBookingEdit}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Salva Modifiche
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Today Collections Popup */}
      <Dialog open={showCollectionPopup} onOpenChange={setShowCollectionPopup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              Incassi da Riscuotere Oggi
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-lg font-semibold text-blue-900">
                {todayCollections.count} prenotazioni da incassare oggi
              </p>
              <p className="text-sm text-slate-600 mt-1">
                Booking.com e Holiday World
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowCollectionPopup(false)}
                variant="outline"
                className="flex-1"
              >
                Chiudi
              </Button>
              <Button
                onClick={() => {
                  setShowCollectionPopup(false);
                  window.location.href = '/prenotazioni-da-incassare';
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Vedi Elenco
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

export default BookingsManager;